/**
 * Centralized Request Manager
 * Handles all database requests with:
 * - Request queuing and prioritization
 * - Automatic deduplication
 * - Rate limit handling with exponential backoff
 * - Request caching
 * - Batch processing
 */

interface QueuedRequest<T> {
  id: string;
  key: string;
  priority: number;
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: any) => void;
  retries: number;
  timestamp: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface RateLimitInfo {
  resetTime: number;
  consecutiveErrors: number;
  isRateLimited: boolean;
}

class RequestManager {
  private queue: QueuedRequest<any>[] = [];
  private cache = new Map<string, CacheEntry<any>>();
  private inFlightRequests = new Map<string, Promise<any>>();
  private processing = false;
  private rateLimitInfo: RateLimitInfo = {
    resetTime: 0,
    consecutiveErrors: 0,
    isRateLimited: false
  };

  // Configuration
  private maxConcurrent = 1; // Max concurrent requests (very conservative for rate limiting)
  private minDelay = 1000; // Min delay between requests (ms) - increased from 500ms
  private lastRequestTime = 0;
  private defaultCacheTTL = 300000; // 5 minutes default cache (increased from 2min)

  /**
   * Execute a request with full protection
   */
  async execute<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: {
      priority?: number;
      cacheTTL?: number;
      skipCache?: boolean;
      skipQueue?: boolean;
    } = {}
  ): Promise<T> {
    const {
      priority = 5,
      cacheTTL = this.defaultCacheTTL,
      skipCache = false,
      skipQueue = false
    } = options;

    // Check cache first
    if (!skipCache && cacheTTL > 0) {
      const cached = this.getFromCache<T>(key);
      if (cached !== null) {
        console.log(`✅ Cache hit for: ${key}`);
        return cached;
      }
    }

    // Check if there's already an in-flight request for this key (deduplication)
    const inFlight = this.inFlightRequests.get(key);
    if (inFlight) {
      console.log(`🔄 Deduplicating request for: ${key}`);
      return inFlight as Promise<T>;
    }

    // Create promise for this request
    const promise = new Promise<T>((resolve, reject) => {
      const requestId = `${key}_${Date.now()}_${Math.random()}`;
      
      this.queue.push({
        id: requestId,
        key,
        priority,
        execute: fetcher,
        resolve,
        reject,
        retries: 0,
        timestamp: Date.now()
      });

      // Sort queue by priority (higher first)
      this.queue.sort((a, b) => b.priority - a.priority);
    });

    // Track in-flight
    this.inFlightRequests.set(key, promise);

    // Cleanup in-flight after completion
    promise
      .then(() => this.inFlightRequests.delete(key))
      .catch(() => this.inFlightRequests.delete(key));

    // Start processing if not already
    if (!this.processing) {
      this.processQueue();
    }

    return promise;
  }

  /**
   * Process the request queue with rate limit handling
   */
  private async processQueue() {
    if (this.processing) return;
    this.processing = true;

    while (this.queue.length > 0) {
      // Wait if we're rate limited
      if (this.rateLimitInfo.isRateLimited && Date.now() < this.rateLimitInfo.resetTime) {
        const waitTime = this.rateLimitInfo.resetTime - Date.now();
        console.log(`⏸️  Rate limited. Waiting ${Math.ceil(waitTime / 1000)}s before continuing...`);
        await this.sleep(waitTime);
        this.rateLimitInfo.isRateLimited = false;
        this.rateLimitInfo.consecutiveErrors = 0;
      }

      // Respect min delay between requests
      const timeSinceLastRequest = Date.now() - this.lastRequestTime;
      if (timeSinceLastRequest < this.minDelay) {
        await this.sleep(this.minDelay - timeSinceLastRequest);
      }

      // Process next request
      const request = this.queue.shift();
      if (request) {
        await this.executeRequest(request);
      }
    }

    this.processing = false;
  }

  /**
   * Execute a single request with retry logic
   */
  private async executeRequest<T>(request: QueuedRequest<T>) {
    this.lastRequestTime = Date.now();

    try {
      const result = await request.execute();

      // Cache the result
      this.cache.set(request.key, {
        data: result,
        timestamp: Date.now(),
        expiresAt: Date.now() + this.defaultCacheTTL
      });

      // Reset rate limit info on success
      this.rateLimitInfo.consecutiveErrors = 0;
      
      // Gradually reduce delay if we're not hitting rate limits
      if (this.minDelay > 500 && this.rateLimitInfo.consecutiveErrors === 0) {
        this.minDelay = Math.max(this.minDelay * 0.9, 500);
      }

      request.resolve(result);
    } catch (error: any) {
      // Handle rate limit errors
      if (this.isRateLimitError(error)) {
        this.rateLimitInfo.consecutiveErrors++;
        const retryAfter = this.getRetryAfter(error);
        
        console.error(`❌ Rate limit error (${this.rateLimitInfo.consecutiveErrors} consecutive):`, error.message);
        
        // Set rate limit reset time
        this.rateLimitInfo.resetTime = Date.now() + retryAfter;
        this.rateLimitInfo.isRateLimited = true;
        
        // Increase delay between requests to prevent future rate limits
        this.minDelay = Math.min(this.minDelay * 1.5, 3000);
        console.log(`⚠️  Increased delay between requests to ${this.minDelay}ms`);
        
        // Retry request with exponential backoff (max 5 retries for rate limits)
        if (request.retries < 5) {
          request.retries++;
          console.log(`🔄 Retrying request (attempt ${request.retries}/5) after ${Math.ceil(retryAfter / 1000)}s`);
          
          // Re-queue at front with high priority
          this.queue.unshift({
            ...request,
            priority: 100 // Highest priority for retries
          });
        } else {
          console.error(`❌ Max retries exceeded for request: ${request.key}`);
          request.reject(new Error(`Rate limit exceeded after 5 retries. Please wait ${Math.ceil(retryAfter / 1000)}s and try again.`));
        }
      } else if (request.retries < 3) {
        // Retry other errors with exponential backoff
        const backoffMs = Math.min(1000 * Math.pow(2, request.retries), 10000);
        console.log(`⚠️  Request failed, retrying in ${backoffMs}ms (attempt ${request.retries + 1}/3)`);
        
        await this.sleep(backoffMs);
        request.retries++;
        this.queue.unshift(request);
      } else {
        console.error(`❌ Request failed after 3 retries:`, error.message);
        request.reject(error);
      }
    }

    // Continue processing queue
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 0);
    }
  }

  /**
   * Check if error is a rate limit error
   */
  private isRateLimitError(error: any): boolean {
    return (
      error?.status === 429 ||
      error?.code === 'RATE_LIMIT_EXCEEDED' ||
      error?.message?.toLowerCase().includes('rate limit') ||
      error?.details?.code === 'RATE_LIMIT_EXCEEDED' ||
      error?.name === 'BlinkNetworkError' && error?.message?.includes('Rate limit')
    );
  }

  /**
   * Extract retry-after duration from error
   */
  private getRetryAfter(error: any): number {
    // Check for reset time in error message
    const resetMatch = error?.message?.match(/Try again in (\d+) seconds/);
    if (resetMatch) {
      const seconds = parseInt(resetMatch[1], 10);
      return (seconds + 3) * 1000; // Add 3 second buffer (increased from 2s)
    }

    // Check for reset time in error details
    if (error?.details?.reset) {
      const resetTime = new Date(error.details.reset).getTime();
      const now = Date.now();
      return Math.max(resetTime - now + 3000, 8000); // Add 3s buffer, min 8s (increased)
    }

    // Check for retry-after header value in seconds
    if (error?.details?.retryAfter) {
      return error.details.retryAfter * 1000 + 3000; // Add 3s buffer
    }

    // Default: exponential backoff based on consecutive errors
    const baseDelay = 8000; // Increased from 5s to 8s
    const multiplier = Math.min(this.rateLimitInfo.consecutiveErrors, 5);
    return baseDelay * Math.pow(1.5, multiplier);
  }

  /**
   * Get cached value if still valid
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Clear cache for specific key or pattern
   */
  clearCache(keyPattern?: string) {
    if (!keyPattern) {
      this.cache.clear();
      console.log('🗑️  Cleared all cache');
      return;
    }

    // Support wildcard clearing
    if (keyPattern.includes('*')) {
      const regex = new RegExp('^' + keyPattern.replace(/\*/g, '.*') + '$');
      const keysToDelete: string[] = [];

      for (const key of this.cache.keys()) {
        if (regex.test(key)) {
          keysToDelete.push(key);
        }
      }

      keysToDelete.forEach(key => this.cache.delete(key));
      console.log(`🗑️  Cleared ${keysToDelete.length} cache entries matching: ${keyPattern}`);
    } else {
      this.cache.delete(keyPattern);
    }
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get queue and cache statistics
   */
  getStats() {
    return {
      queueLength: this.queue.length,
      inFlightRequests: this.inFlightRequests.size,
      cacheSize: this.cache.size,
      isRateLimited: this.rateLimitInfo.isRateLimited,
      consecutiveErrors: this.rateLimitInfo.consecutiveErrors,
      minDelay: this.minDelay,
      processing: this.processing
    };
  }

  /**
   * Clear expired cache entries
   */
  clearExpired() {
    const now = Date.now();
    let cleared = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        cleared++;
      }
    }

    if (cleared > 0) {
      console.log(`🧹 Cleared ${cleared} expired cache entries`);
    }
  }
}

// Export singleton instance
export const requestManager = new RequestManager();

// Auto-cleanup expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    requestManager.clearExpired();
  }, 5 * 60 * 1000);
}

/**
 * Wrapper for Blink DB operations with automatic request management
 */
export function withRequestManagement<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  key: string,
  options?: {
    priority?: number;
    cacheTTL?: number;
    skipCache?: boolean;
  }
): T {
  return ((...args: any[]) => {
    const cacheKey = `${key}:${JSON.stringify(args)}`;
    return requestManager.execute(
      cacheKey,
      () => fn(...args),
      options
    );
  }) as T;
}
